import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import type { EmailDeliveryView, TestStatusView } from "@shared/types/notifications";
import {
  EMAIL_DELIVERY_QUERY_KEY,
  useEmailTestStatus,
  useSendTestEmail,
} from "@/hooks/use-notifications";
import type { ApiClientError } from "@/lib/api";
import {
  EMAIL_TEST_NO_ACCOUNT_EMAIL,
  EMAIL_TEST_WAITING_TOO_LONG,
  emailTestRateLimitedSentence,
  emailTestResultSentence,
  retryAtOf,
} from "@/lib/notifications/sentences";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { PlatformTestDialog } from "./PlatformTestDialog";

/**
 * "Send a test email" (contract-notif R9, §4.5; round-1 ruling 9).
 *
 * The test goes to the calling PlatformAdmin's own account address, never a typed one, so the
 * dialog has no field: it names the masked address and offers "Send". The reading of the result
 * is `PlatformTestDialog`'s (#2158: a delivery that ends for good after 90 s is still said).
 */
export function SendTestEmailDialog({
  open,
  onOpenChange,
  view,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  view: EmailDeliveryView;
}) {
  const zone = useDisplayTz();
  const queryClient = useQueryClient();
  const send = useSendTestEmail();
  const onResult = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: EMAIL_DELIVERY_QUERY_KEY });
  }, [queryClient]);

  function refusal(error: ApiClientError): string | null {
    if (error.status === 429) return emailTestRateLimitedSentence(retryAtOf(error.details), zone);
    if (error.status === 409) return EMAIL_TEST_NO_ACCOUNT_EMAIL;
    return null;
  }

  return (
    <PlatformTestDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Send a test email"
      testIdRoot="send-test-email"
      intro={
        view.callerEmailMasked
          ? `It goes to your own address, ${view.callerEmailMasked}.`
          : "It goes to your own account address."
      }
      channel="email"
      send={send}
      useStatus={useEmailTestStatus}
      resultSentence={(answer: TestStatusView) =>
        emailTestResultSentence(
          answer,
          { maskedTo: view.callerEmailMasked, vault: view.vaultName, sender: view.sender },
          zone,
        )
      }
      waitingTooLong={EMAIL_TEST_WAITING_TOO_LONG}
      context={{ vault: view.vaultName, sender: view.sender }}
      refusalSentence={refusal}
      queueFailure="The test email could not be queued."
      statusFailure="The test email's status did not load."
      onResult={onResult}
    />
  );
}
